import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useFactDetailSuggestionsContext, useFactDetailContext } from "../context";
import { useFactsContext } from "../../../context";
import { useDebounce } from "../../../../../../../../hooks";
import { approveSuggestion, rejectSuggestion } from "../../../../../services";
import dayjs from "dayjs";

// Helper para formatear fecha
const formatDate = (dateString) => {
  if (!dateString) return "—";
  return dayjs(dateString).format("MMM D, YYYY");
};


export const useFactDetailSuggestions = (factId) => {
  // ============================================================================
  // External Hooks
  // ============================================================================
  const { knowledgeId } = useParams();
  const {
    baseSuggestions,
    baseMeta,
    baseKnowledgebaseUuid,
    baseFactUuid,
    suggestions,
    suggestionsMeta,
    loading,
    error,
    fetchSuggestions,
    resetSuggestionsToBase,
    resetSuggestions,
    removeSuggestion,
  } = useFactDetailSuggestionsContext();
  const { fetchFactDetail } = useFactDetailContext();
  const { fetchFacts } = useFactsContext();

  // ============================================================================
  // State - Filter States
  // ============================================================================
  const [searchValue, setSearchValue] = useState("");

  // ============================================================================
  // Debounced Search Value
  // ============================================================================
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const isSearching = searchValue !== debouncedSearchValue;

  // ============================================================================
  // State - Pagination & Sorting
  // ============================================================================
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDirection, setOrderDirection] = useState('DESC');

  // ============================================================================
  // State - UI State
  // ============================================================================
  const [searchKey, setSearchKey] = useState(0);

  // ============================================================================
  // State - Alert State
  // ============================================================================
  const [alert, setAlert] = useState(null);

  // ============================================================================
  // State - Internal State
  // ============================================================================
  const [hasLoadedBase, setHasLoadedBase] = useState(false);
  const prevFactIdRef = useRef(null);
  const prevKnowledgeIdRef = useRef(null);
  const prevSanitizedSearchRef = useRef(null);

  // ============================================================================
  // useMemo - Derived Data (for API calls)
  // ============================================================================
  const sanitizedSearch = useMemo(() => {
    const trimmed = debouncedSearchValue.trim();
    return trimmed.length ? trimmed : null;
  }, [debouncedSearchValue]);

  // ============================================================================
  // useMemo - Check if there are active filters
  // ============================================================================
  const hasActiveFilters = useMemo(() => {
    return Boolean(sanitizedSearch);
  }, [sanitizedSearch]);

  // ============================================================================
  // useMemo - Transform suggestions (simplified - keep original keys)
  // ============================================================================
  const transformedSuggestions = useMemo(() => {
    return suggestions.map((suggestion) => ({
      ...suggestion,
      // Solo agregamos campos derivados mínimos necesarios para la UI
      formatted_date: formatDate(suggestion.created_at),
    }));
  }, [suggestions]);

  // ============================================================================
  // Utility Callbacks
  // ============================================================================
  const resetLocalState = useCallback(() => {
    setSearchValue("");
    setPage(1);
    setPageSize(20);
    setOrderBy('created_at');
    setOrderDirection('DESC');
  }, []);

  // ============================================================================
  // useEffect Hooks
  // ============================================================================
  useEffect(() => {
    if (!factId || !knowledgeId) return;

    // Validate baseKnowledgebaseUuid or baseFactUuid mismatch
    if (
      (baseKnowledgebaseUuid && baseKnowledgebaseUuid !== knowledgeId) ||
      (baseFactUuid && baseFactUuid !== factId)
    ) {
      resetSuggestions();
    }

    // Validate prevKnowledgeIdRef or prevFactIdRef mismatch
    if (
      (prevKnowledgeIdRef.current && prevKnowledgeIdRef.current !== knowledgeId) ||
      (prevFactIdRef.current && prevFactIdRef.current !== factId)
    ) {
      resetSuggestions();
    }

    prevKnowledgeIdRef.current = knowledgeId;
    prevFactIdRef.current = factId;
    setHasLoadedBase(false);
    resetLocalState();
  }, [factId, knowledgeId, baseKnowledgebaseUuid, baseFactUuid, resetSuggestions, resetLocalState]);

  useEffect(() => {
    if (!factId || !knowledgeId) return;

    const hasFilters = Boolean(sanitizedSearch);
    const hasSorting = orderBy !== 'created_at' || orderDirection !== 'DESC';
    const hasPaginationChanges = page !== 1 || pageSize !== 20;

    const searchWasCleared = 
      prevSanitizedSearchRef.current !== null && 
      sanitizedSearch === null;

    const params = {
      knowledgebase_uuid: knowledgeId,
      fact_uuid: factId,
      page,
      page_size: pageSize,
      order_by: orderBy,
      order_direction: orderDirection,
      search: sanitizedSearch ? { keyword: sanitizedSearch } : undefined,
    };

    if (!hasFilters && !hasSorting && !hasPaginationChanges) {
      if (!hasLoadedBase) {
        if (baseSuggestions.length) {
          resetSuggestionsToBase();
          setHasLoadedBase(true);
          prevSanitizedSearchRef.current = sanitizedSearch;
          return;
        }

        fetchSuggestions(params, { persistAsBase: true });
        setHasLoadedBase(true);
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      if (searchWasCleared) {
        resetSuggestionsToBase();
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      prevSanitizedSearchRef.current = sanitizedSearch;
      return;
    }

    fetchSuggestions(params, { persistAsBase: false });
    prevSanitizedSearchRef.current = sanitizedSearch;
  }, [
    factId,
    knowledgeId,
    page,
    pageSize,
    orderBy,
    orderDirection,
    sanitizedSearch,
    fetchSuggestions,
    hasLoadedBase,
    baseSuggestions.length,
    resetSuggestionsToBase,
  ]);

  // ============================================================================
  // Handlers - Search Handlers
  // ============================================================================
  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  // ============================================================================
  // Handlers - Clear Handlers
  // ============================================================================
  const handleClearAllFilters = useCallback(() => {
    setSearchValue("");
    setPage(1);
    setSearchKey((prev) => prev + 1);
    resetSuggestionsToBase();
  }, [resetSuggestionsToBase]);

  const handleClearAllFiltersWithKeys = useCallback(() => {
    handleClearAllFilters();
  }, [handleClearAllFilters]);

  // ============================================================================
  // Handlers - Pagination Handlers
  // ============================================================================
  const handlePageChange = useCallback(
    (nextPage) => {
      setPage(nextPage);

      if (nextPage === 1) {
        const hasFilters = Boolean(sanitizedSearch);
        const isDefaultSorting = orderBy === 'created_at' && orderDirection === 'DESC';

        if (!hasFilters && isDefaultSorting) {
          resetSuggestionsToBase();
        }
      }
    },
    [
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetSuggestionsToBase,
    ]
  );

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage) => {
      setPageSize(itemsPerPage);
      setPage(1);

      if (itemsPerPage === 20) {
        const hasFilters = Boolean(sanitizedSearch);
        const isDefaultSorting = orderBy === 'created_at' && orderDirection === 'DESC';

        if (!hasFilters && isDefaultSorting) {
          resetSuggestionsToBase();
        }
      }
    },
    [
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetSuggestionsToBase,
    ]
  );

  // ============================================================================
  // Handlers - UI Handlers
  // ============================================================================
  const handleApprove = useCallback(async (groupId, suggestion_uuid) => {
    try {
      await approveSuggestion(suggestion_uuid);
      // Remover suggestion del estado local
      removeSuggestion(suggestion_uuid);
      
      // Refrescar fact detail después de aprobar exitosamente
      if (factId) {
        fetchFactDetail(factId);
      }
      
      // Refrescar facts después de aprobar exitosamente
      if (knowledgeId) {
        fetchFacts({
          department_uuid: knowledgeId,
          page: 1,
          page_size: 20,
          order_by: 'updated_at',
          order_direction: 'DESC',
        }, { persistAsBase: true });
      }
      
      // Mostrar alert de éxito
      setAlert({
        title: 'Success',
        description: 'Suggestion approved successfully.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error approving suggestion:', error);
      // Mostrar alert de error
      setAlert({
        title: 'Error',
        description: 'Failed to approve suggestion. Please try again.',
        severity: 'error',
      });
    }
  }, [removeSuggestion, factId, knowledgeId, fetchFactDetail, fetchFacts]);

  const handleDecline = useCallback(async (groupId, suggestion_uuid) => {
    try {
      await rejectSuggestion(suggestion_uuid);
      // Remover suggestion del estado local
      removeSuggestion(suggestion_uuid);
      // Mostrar alert de éxito
      setAlert({
        title: 'Success',
        description: 'Suggestion rejected successfully.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
      // Mostrar alert de error
      setAlert({
        title: 'Error',
        description: 'Failed to reject suggestion. Please try again.',
        severity: 'error',
      });
    }
  }, [removeSuggestion]);

  const handleCloseAlert = useCallback(() => {
    setAlert(null);
  }, []);

  const handleAddSuggestion = useCallback(() => {
    // TODO: Implementar funcionalidad de agregar sugerencia
    console.log("Add suggestion for fact", factId);
  }, [factId]);

  // ============================================================================
  // Return
  // ============================================================================
  return {
    loading: loading || isSearching,
    suggestions: transformedSuggestions,
    currentPage: page,
    itemsPerPage: pageSize,
    totalItems: suggestionsMeta?.totalItems || 0,
    totalPages: suggestionsMeta?.totalPages || 0,
    filters: {
      searchValue,
      hasActiveFilters,
    },
    filterHandlers: {
      handleSearchChange,
      handleClearAllFiltersWithKeys,
    },
    filterUI: {
      searchKey,
    },
    handlePageChange,
    handleItemsPerPageChange,
    handleApprove,
    handleDecline,
    handleAddSuggestion,
    alert,
    handleCloseAlert,
  };
};

